// Taking an Assessment, in the same flow and design as a course lesson: the
// full-bleed page with its own header, a left panel, a stepper and one
// conversation with Elliott, built from the lesson's own pieces (Elliott's
// messages typed out, the quiz card, the reply bar, two-way voice). What differs:
//   - the left panel lists capabilities (there are no modules or lessons);
//   - the stepper is Roleplay -> Quiz, for the capability in hand;
//   - each Roleplay is ONE attempt, with no coaching and no feedback: an
//     Assessment captures where someone is today. Lesson Roleplays coach, so
//     they allow several attempts;
//   - nothing is marked right or wrong until the summary at the end.
//
// The Assessment, and what the learner has answered, come from the backend
// (GET /v3/member/assessments/{id}); each Roleplay answer is graded by Elliott
// when it is sent, and the summary is the backend's results, which only exist
// once every item is answered.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { LuArrowRight, LuCheck, LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import dosenLogo from '../../../../../assets/images/brand/dosen/white-logo.svg';
import { Bone, fadeInSx } from '../../../../../components/ui/DashboardSkeletons';
import { formatDate } from '../../../../../utils/formatDate';
import { useAppSelector } from '../../../../../redux/hooks';
import { isElliottVoiceEnabled } from '../../../../../utils/elliottVoiceFlag';
import { getUserDetails } from '../../../../../utils/orgName';
import ElliottMessage, { messageBodySx } from '../../home/elliott-lesson/components/ElliottMessage';
import InputBar from '../../home/elliott-lesson/components/InputBar';
import LearnerMessage from '../../home/elliott-lesson/components/LearnerMessage';
import QuickReplyPill from '../../home/elliott-lesson/components/QuickReplyPill';
import QuizCard, { type QuizCheckResult } from '../../home/elliott-lesson/components/QuizCard';
import StepStepper, { type Step } from '../../home/elliott-lesson/components/StepStepper';
import StreamingText from '../../home/elliott-lesson/components/StreamingText';
import { elliottTokens as t } from '../../home/elliott-lesson/tokens';
import { useVoiceConversation } from '../../home/elliott-lesson/voice/useVoiceConversation';
import VoiceBar from '../../home/elliott-lesson/voice/VoiceBar';
import { isVoiceConversationSupported } from '../../home/elliott-lesson/voice/voiceEngine';
import { LEVEL_COLOR, LEVEL_NAME } from '../../Program/components/learning-lifecycle/CapabilityGapsList';
import { apiMessage, capabilityApi } from '../../../../../api/capability';
import { useMyAssessment, useMyAssessmentResults, useRefreshCapability } from '../../../../../queries/useCapability';
import { levelChange } from './learnerStore';
import { MOBILE_SX, useIsMobileApp } from '../../../../../mobile/mobileApp';

const PATHWAY_ROUTE = '/app/home/user';
const ONE_ATTEMPT = 'Take your time. You answer each Roleplay once, so send your answer when you are happy with it.';
type ItemStatus = 'complete' | 'in-progress' | 'todo';
/** What the learner has answered on one capability: their Roleplay and, after it, their quiz choice. */
interface Answer {
  roleplay?: { id: string; response: string };
  quizAnswer?: number;
}
type Answers = Record<string, Answer>;

const isDone = (answers: Answers, capabilityId: string) => {
  const done = answers[capabilityId];
  return Boolean(done?.roleplay && done.quizAnswer !== undefined);
};
const itemStatus = (answers: Answers, capabilityId: string, active: boolean): ItemStatus => {
  if (isDone(answers, capabilityId) && !active) return 'complete';
  return active || answers[capabilityId]?.roleplay ? 'in-progress' : 'todo';
};
// A calendar date ("2026-09-30"), read as that day wherever the learner is.
const dateText = (iso: string) => formatDate(`${iso}T00:00:00`);

/** Back to the learner's dashboard: navigation only, so an orange text link. */
const PlanLink: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      p: 0,
      border: 'none',
      bgcolor: 'transparent',
      color: t.brandLight,
      fontFamily: 'Inter, sans-serif',
      fontSize: 15,
      fontWeight: 600,
      cursor: 'pointer',
      '&:hover': { textDecoration: 'underline' }
    }}
  >
    Dashboard
  </Box>
);

/** One capability in the left panel, in the lesson list's colours: green done, brandy in hand, grey to do. */
const CapabilityRow: React.FC<{ title: string; status: ItemStatus }> = ({ title, status }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      minHeight: 40,
      px: '12px',
      borderRadius: '6px',
      background: status === 'complete' ? t.navGreenFill : status === 'in-progress' ? t.navBrandyFill : 'transparent'
    }}
  >
    <Box sx={{ width: 16, height: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {status === 'complete' && <LuCheck size={15} color={t.textPrimary} strokeWidth={3} />}
      {status === 'in-progress' && <LuArrowRight size={15} color={t.textPrimary} strokeWidth={3} />}
      {status === 'todo' && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.textTertiary }} />}
    </Box>
    <Typography
      sx={{
        flex: 1,
        minWidth: 0,
        fontSize: 14,
        fontWeight: status === 'todo' ? 400 : 600,
        color: status === 'complete' ? t.navGreenText : status === 'in-progress' ? t.navBrandyText : t.textSecondary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      {title}
    </Typography>
  </Box>
);

/** A labelled line of a result card on the summary. */
const ResultLine: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  // Phone: the label over its value, which then has the card's whole width.
  <Box
    sx={{ display: 'flex', alignItems: 'baseline', gap: '14px', [MOBILE_SX]: { flexDirection: 'column', gap: '6px' } }}
  >
    <Typography
      sx={{
        width: 150,
        [MOBILE_SX]: { width: 'auto' },
        flexShrink: 0,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: t.textTertiary
      }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Box>
);
const LevelWord: React.FC<{ level: keyof typeof LEVEL_NAME | null }> = ({ level }) =>
  level ? (
    <Box
      component="span"
      sx={{ color: LEVEL_COLOR[level], fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}
    >
      {LEVEL_NAME[level]}
    </Box>
  ) : (
    <Box component="span" sx={{ color: t.textTertiary }}>
      Not assessed
    </Box>
  );

const AssessmentPlayerPage: React.FC = () => {
  const navigate = useNavigate();
  const isMobileApp = useIsMobileApp();
  const { assessmentId = '' } = useParams<{ assessmentId: string }>();
  const { data: assessment, isLoading } = useMyAssessment(assessmentId);
  const refresh = useRefreshCapability();
  // What the learner has answered: from the backend when the page opens, then as each answer is accepted.
  const [answers, setAnswers] = useState<Answers>({});
  const [loaded, setLoaded] = useState(false);
  // What was already there when the page opened is shown at once; only new messages type out.
  const [restored, setRestored] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!assessment || loaded) return;
    const initial: Answers = {};
    for (const i of assessment.items) {
      initial[i.capabilityId] = {
        ...(i.progress.roleplay ? { roleplay: i.progress.roleplay } : {}),
        ...(i.progress.quizAnswer !== null ? { quizAnswer: i.progress.quizAnswer } : {})
      };
    }
    setAnswers(initial);
    setRestored(new Set(Object.keys(initial).filter((id) => initial[id].roleplay)));
    setLoaded(true);
  }, [assessment, loaded]);
  const [draft, setDraft] = useState('');
  // The capability just finished stays on screen until the learner moves on.
  const [holdId, setHoldId] = useState<string | null>(null);
  // Elliott is grading the answer just sent; or the backend refused it.
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const items = assessment?.items ?? [];
  const firstOpen = items.findIndex((i) => !isDone(answers, i.capabilityId));
  const heldAt = holdId ? items.findIndex((i) => i.capabilityId === holdId) : -1;
  const at = heldAt >= 0 ? heldAt : firstOpen;
  const finished = loaded && items.length > 0 && firstOpen === -1 && heldAt === -1;
  const item = at >= 0 ? items[at] : null;
  const done: Answer = item ? answers[item.capabilityId] ?? {} : {};
  const answered = done.roleplay;
  const quizAnswered = done.quizAnswer !== undefined;
  const instant = Boolean(item && restored.has(item.capabilityId));
  const { data: results } = useMyAssessmentResults(assessmentId, finished);

  // ── Voice: Elliott speaks the situation, the learner answers aloud, as in a lesson's Roleplay ──
  const orgData = useAppSelector((state) => state.orgDetails.data);
  const voiceAvailable =
    isElliottVoiceEnabled(orgData?._id, orgData?.authId, getUserDetails().orgId) && isVoiceConversationSupported();
  const [voiceMode, setVoiceMode] = useState(false);
  const [voiceDenied, setVoiceDenied] = useState(false);
  const [repeating, setRepeating] = useState(false);
  const voiceModeRef = useRef(false);
  voiceModeRef.current = voiceMode;
  // Spoken phrases land in the same field as typing; read through a ref, since they can arrive before a render.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const setDraftNow = (next: string) => {
    draftRef.current = next;
    setDraft(next);
  };

  // The one attempt: graded by Elliott as it is sent, never shown back as right or wrong here.
  const sendRoleplay = (text: string) => {
    const response = text.trim();
    if (!item || !assessment || answered || !response || sending) return false;
    const { capabilityId } = item;
    setSending(true);
    setSendError(null);
    setVoiceMode(false);
    capabilityApi
      .answerAssessmentRoleplay(assessment.id, capabilityId, {
        response,
        inputMode: voiceModeRef.current ? 'voice' : 'text'
      })
      .then((r) => {
        setAnswers((prev) => ({
          ...prev,
          [capabilityId]: { ...prev[capabilityId], roleplay: { id: r.roleplayId, response } }
        }));
        setDraftNow('');
      })
      .catch((err) => setSendError(apiMessage(err, 'Your answer could not be sent. Send it again.')))
      .finally(() => setSending(false));
    return true;
  };
  const chooseAnswer = (index: number) => {
    if (!item || !assessment || quizAnswered || sending) return;
    const { capabilityId } = item;
    setSending(true);
    setSendError(null);
    capabilityApi
      .answerAssessmentQuiz(assessment.id, capabilityId, index)
      .then((r) => {
        setAnswers((prev) => ({ ...prev, [capabilityId]: { ...prev[capabilityId], quizAnswer: index } }));
        setHoldId(capabilityId);
        // The dashboard's card and the learner's levels move on with every answer.
        refresh('mine');
        if (r.assessmentCompleted) refresh('admin');
      })
      .catch((err) => setSendError(apiMessage(err, 'Your answer could not be saved. Choose it again.')))
      .finally(() => setSending(false));
  };

  const voice = useVoiceConversation({
    active: voiceMode,
    onTranscript: (text) => {
      const kept = draftRef.current.trimEnd();
      setDraftNow(kept ? `${kept} ${text}` : text);
    },
    onReadyToSend: () => sendRoleplay(draftRef.current),
    onMicDenied: () => {
      setVoiceMode(false);
      setVoiceDenied(true);
    },
    getSttContext: () => (item?.roleplay ?? '').slice(0, 500)
  });
  const speakSituation = (onSettled?: () => void) => {
    if (!item) return;
    voice.speak(item.roleplay, {
      onStart: () => undefined,
      onSpoken: () => {
        onSettled?.();
        if (voiceModeRef.current) voice.listen();
      }
    });
  };
  // Turning voice on: Elliott reads the situation aloud, then the mic opens.
  useEffect(() => {
    if (voiceMode) speakSituation();
    else setRepeating(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceMode]);

  const steps: Step[] = [
    { key: 'simulation', label: 'Roleplay', status: finished || answered ? 'complete' : 'active' },
    { key: 'quiz', label: 'Quiz', status: finished || quizAnswered ? 'complete' : answered ? 'active' : 'upcoming' }
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        bgcolor: t.bg,
        position: 'relative',
        overflow: 'hidden',
        [MOBILE_SX]: { height: '100dvh' }
      }}
    >
      {/* Header: where the learner is (left), the Dosen logo (right), as on a lesson. */}
      {isMobileApp ? (
        // Phone: an app bar, back to the Dashboard and the Assessment's name in full.
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            minHeight: 56,
            px: '6px',
            py: '6px',
            pt: 'calc(6px + env(safe-area-inset-top))',
            borderBottom: `1px solid ${t.borderDim}`
          }}
        >
          <Box
            role="button"
            aria-label="Back to Dashboard"
            onClick={() => navigate(PATHWAY_ROUTE)}
            sx={{
              flexShrink: 0,
              width: 40,
              height: 40,
              display: 'grid',
              placeItems: 'center',
              color: t.textPrimary,
              cursor: 'pointer'
            }}
          >
            <LuChevronLeft size={24} />
          </Box>
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              textAlign: 'center',
              fontSize: 15,
              fontWeight: 600,
              lineHeight: 1.3,
              color: t.textPrimary
            }}
          >
            {assessment?.name ?? 'Assessment'}
          </Typography>
          <Box sx={{ width: 40, flexShrink: 0 }} />
        </Box>
      ) : (
        <Box
          sx={{
            height: 86,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: '32px',
            borderBottom: `1px solid ${t.borderDim}`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <Typography
              onClick={() => navigate(PATHWAY_ROUTE)}
              sx={{
                fontSize: 16,
                color: t.textSecondary,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                '&:hover': { color: t.textPrimary }
              }}
            >
              Dashboard
            </Typography>
            <LuChevronRight size={18} color={t.textTertiary} style={{ flexShrink: 0 }} />
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 600,
                color: t.textPrimary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {assessment?.name ?? 'Assessment'}
            </Typography>
          </Box>
          <Box
            component="img"
            src={dosenLogo}
            alt="Dosen"
            sx={{ height: 36, width: 92, objectFit: 'contain', flexShrink: 0 }}
          />
        </Box>
      )}

      {!assessment && isLoading ? (
        <Box
          role="status"
          aria-label="Loading the Assessment"
          sx={{ flex: 1, minHeight: 0, display: 'flex', [MOBILE_SX]: { flexDirection: 'column' } }}
        >
          <Box
            sx={{
              width: 300,
              flexShrink: 0,
              borderRight: `1px solid ${t.borderDim}`,
              p: '20px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              [MOBILE_SX]: { width: '100%', borderRight: 'none', p: '12px 16px' }
            }}
          >
            <Bone width={110} height={12} radius="4px" />
            {[0, 1, 2, 3].map((i) => (
              <Bone key={i} height={40} radius="6px" />
            ))}
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              px: '130px',
              py: '25px',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              [MOBILE_SX]: { px: '16px', py: '12px' }
            }}
          >
            <Bone height={28} radius="14px" />
            <Bone width="70%" height={96} radius="8px" />
            <Bone width="55%" height={64} radius="8px" />
          </Box>
        </Box>
      ) : !assessment ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            [MOBILE_SX]: { px: '24px', textAlign: 'center' }
          }}
        >
          <Typography sx={{ fontSize: 15, color: t.textSecondary }}>
            This Assessment is no longer open. Go back to your Dashboard to see what is next.
          </Typography>
          <PlanLink onClick={() => navigate(PATHWAY_ROUTE)} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', [MOBILE_SX]: { flexDirection: 'column' } }}>
          {/* Left panel: the capabilities, where a lesson lists its modules and lessons.
              Phone: one row along the top that scrolls sideways, each capability in full. */}
          <Box
            sx={{
              width: 300,
              flexShrink: 0,
              borderRight: `1px solid ${t.borderDim}`,
              p: '20px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              overflowY: 'auto',
              [MOBILE_SX]: {
                width: '100%',
                flexDirection: 'row',
                overflowX: 'auto',
                overflowY: 'hidden',
                borderRight: 'none',
                borderBottom: `1px solid ${t.borderDim}`,
                p: '8px 12px',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                '& > *': { flex: '0 0 auto' },
                '& .MuiTypography-root': { overflow: 'visible' }
              }
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: t.textTertiary,
                px: '12px',
                pb: '8px',
                [MOBILE_SX]: { display: 'none' }
              }}
            >
              Capabilities
            </Typography>
            {items.map((i, index) => (
              <CapabilityRow
                key={i.capabilityId}
                title={i.capabilityName}
                status={itemStatus(answers, i.capabilityId, index === at && !quizAnswered)}
              />
            ))}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flexShrink: 0, py: '25px', px: '130px', [MOBILE_SX]: { py: '12px', px: '8px' } }}>
              <StepStepper steps={steps} />
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: '130px', [MOBILE_SX]: { px: '16px' } }}>
              <Box
                sx={{
                  maxWidth: 1146,
                  mx: 'auto',
                  pt: '20px',
                  pb: '40px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  [MOBILE_SX]: { pt: '8px', pb: '24px', gap: '16px' }
                }}
              >
                {finished ? (
                  // The summary: what was captured, Elliott's analysis, and what it did to each level.
                  <>
                    <ElliottMessage stacked>
                      <StreamingText
                        sx={messageBodySx}
                        paragraphs={[
                          'You have completed the Assessment. Thank you.',
                          'Here are your results for each capability: your level in this Roleplay, my analysis, and your overall score. Your overall score comes from your five most recent Roleplays on that capability.'
                        ]}
                      />
                    </ElliottMessage>

                    {!results && (
                      <Typography
                        role="status"
                        sx={{ ...messageBodySx, color: t.textTertiary, pl: '60px', [MOBILE_SX]: { pl: 0 } }}
                      >
                        Gathering your results...
                      </Typography>
                    )}
                    {(results?.items ?? []).map((r) => {
                      const change = levelChange(r.previousLevel, r.levelNow);
                      return (
                        <Box
                          key={r.capabilityId}
                          sx={{
                            ml: '60px',
                            mr: '68px',
                            [MOBILE_SX]: { ml: 0, mr: 0 },
                            borderRadius: '8px',
                            border: `1px solid ${t.borderDim}`,
                            bgcolor: t.surface,
                            ...fadeInSx
                          }}
                        >
                          <Box
                            sx={{
                              px: '20px',
                              py: '14px',
                              borderBottom: `1px solid ${t.borderDim}`,
                              [MOBILE_SX]: { px: '14px' }
                            }}
                          >
                            <Typography sx={{ fontSize: 17, fontWeight: 600, color: t.textPrimary }}>
                              {r.capabilityName}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              p: '16px 20px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '14px',
                              [MOBILE_SX]: { p: '14px' }
                            }}
                          >
                            <ResultLine label="This Assessment">
                              <Typography sx={messageBodySx}>
                                <LevelWord level={r.roleplay.level} />
                              </Typography>
                            </ResultLine>
                            <ResultLine label="Your Score Now">
                              <Typography sx={messageBodySx}>
                                <LevelWord level={r.levelNow} />
                                {change && (
                                  <Box component="span" sx={{ color: t.textSecondary }}>
                                    {'  '}
                                    {change.text.replace('with your most recent Roleplay.', 'with this Assessment.')}
                                  </Box>
                                )}
                                {!change && r.levelNow && (
                                  <Box component="span" sx={{ color: t.textSecondary }}>
                                    {'  '}This is your first Roleplay on this capability, so it sets your score.
                                  </Box>
                                )}
                              </Typography>
                            </ResultLine>
                            <ResultLine label="Elliott's Analysis">
                              <Typography sx={messageBodySx}>{r.roleplay.analysis}</Typography>
                            </ResultLine>
                            <ResultLine label="Recent Roleplays">
                              {/* The five that set the level, this Assessment's picked out. */}
                              <Box sx={{ border: `1px solid ${t.borderDim}`, borderRadius: '6px', overflow: 'hidden' }}>
                                {r.recentRoleplays.map((rp, index) => {
                                  const mine = rp.id === r.roleplay.id;
                                  return (
                                    <Box
                                      key={rp.id}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '14px',
                                        minHeight: 38,
                                        px: '12px',
                                        [MOBILE_SX]: { flexWrap: 'wrap', rowGap: '2px', py: '8px' },
                                        ...(index > 0 ? { borderTop: `1px solid ${t.borderDim}` } : {}),
                                        background: mine ? t.navBrandyFill : 'transparent'
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          width: 110,
                                          [MOBILE_SX]: { width: 84 },
                                          flexShrink: 0,
                                          fontSize: 12,
                                          fontWeight: 700,
                                          letterSpacing: '0.04em',
                                          textTransform: 'uppercase',
                                          color: mine ? t.navBrandyText : LEVEL_COLOR[rp.level]
                                        }}
                                      >
                                        {LEVEL_NAME[rp.level]}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          width: 96,
                                          [MOBILE_SX]: { width: 'auto' },
                                          flexShrink: 0,
                                          fontSize: 13,
                                          color: mine ? t.navBrandyText : t.textSecondary
                                        }}
                                      >
                                        {dateText(rp.completedOn)}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          flex: 1,
                                          minWidth: 0,
                                          fontSize: 13,
                                          color: t.textPrimary,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          // Phone: where it came from, in full, on a line of its own.
                                          [MOBILE_SX]: { flexBasis: '100%', whiteSpace: 'normal', overflow: 'visible' }
                                        }}
                                      >
                                        {mine ? 'This Assessment' : rp.source}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </ResultLine>
                            <ResultLine label="Quiz Question">
                              {r.quiz.passed ? (
                                <Typography sx={{ ...messageBodySx, color: t.success, fontWeight: 600 }}>
                                  Correct
                                </Typography>
                              ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: '28px', rowGap: '6px' }}>
                                  <Typography sx={{ ...messageBodySx, color: t.textSecondary }}>
                                    Your answer:{' '}
                                    {items.find((i) => i.capabilityId === r.capabilityId)?.quiz.options[
                                      r.quiz.answerIndex
                                    ] ?? 'Not set'}
                                  </Typography>
                                  <Typography sx={{ ...messageBodySx, color: t.textPrimary }}>
                                    Correct answer: {r.quiz.correctOption}
                                  </Typography>
                                </Box>
                              )}
                            </ResultLine>
                          </Box>
                        </Box>
                      );
                    })}

                    <ElliottMessage stacked>
                      <Typography sx={messageBodySx}>
                        Your scores are now on your Dashboard, under Capability, where you can follow how each one
                        changes.
                      </Typography>
                      <Typography sx={messageBodySx}>
                        To raise a score, open the capability there and warm up with a Practice Roleplay, which is never
                        graded. When you are ready, take a Scored Roleplay: it counts toward your score.
                      </Typography>
                    </ElliottMessage>
                    <Box sx={{ pl: '60px', [MOBILE_SX]: { pl: 0 } }}>
                      <PlanLink onClick={() => navigate(PATHWAY_ROUTE)} />
                    </Box>
                  </>
                ) : (
                  item && (
                    <React.Fragment key={item.capabilityId}>
                      <ElliottMessage stacked>
                        <StreamingText
                          sx={messageBodySx}
                          enabled={!instant && !answered}
                          paragraphs={[
                            `${item.capabilityName} (${at + 1} of ${items.length})`,
                            item.roleplay,
                            ONE_ATTEMPT
                          ]}
                        />
                      </ElliottMessage>

                      {sending && !answered && (
                        <Box sx={{ pl: '60px', [MOBILE_SX]: { pl: 0 } }}>
                          <Typography
                            role="status"
                            sx={{ ...messageBodySx, color: t.textTertiary, fontStyle: 'italic' }}
                          >
                            Elliott is reading your answer...
                          </Typography>
                        </Box>
                      )}
                      {sendError && (
                        <Box sx={{ pl: '60px', [MOBILE_SX]: { pl: 0 } }}>
                          <Typography role="alert" sx={{ ...messageBodySx, color: t.error }}>
                            {sendError}
                          </Typography>
                        </Box>
                      )}
                      {answered && (
                        <>
                          <LearnerMessage>{answered.response}</LearnerMessage>
                          <ElliottMessage stacked>
                            <StreamingText
                              sx={messageBodySx}
                              enabled={!instant}
                              paragraphs={['Thank you, I have that. One question to finish this capability.']}
                            />
                          </ElliottMessage>
                          {/* The lesson's quiz card. No result is passed, so nothing is marked until the summary. */}
                          <Box sx={{ pl: '60px', pr: '68px', [MOBILE_SX]: { pl: 0, pr: 0 } }}>
                            <QuizCard
                              question={item.quiz.question}
                              options={item.quiz.options.map((label, index) => ({ id: String(index), label }))}
                              selectedId={quizAnswered ? String(done.quizAnswer) : null}
                              result={null as QuizCheckResult | null}
                              instant={instant || quizAnswered}
                              onSelect={(id) => chooseAnswer(Number(id))}
                            />
                          </Box>
                          {quizAnswered && (
                            // Phone: the next step pinned along the bottom, full width.
                            <Box
                              sx={{
                                pl: '60px',
                                ...fadeInSx,
                                [MOBILE_SX]: {
                                  position: 'sticky',
                                  bottom: 0,
                                  pl: 0,
                                  py: '10px',
                                  bgcolor: t.bg,
                                  display: 'flex',
                                  '& > *': { flex: 1, minHeight: 46 }
                                }
                              }}
                            >
                              <QuickReplyPill
                                label={firstOpen === -1 ? 'See My Results' : 'Next Capability'}
                                onClick={() => setHoldId(null)}
                              />
                            </Box>
                          )}
                        </>
                      )}
                    </React.Fragment>
                  )
                )}
              </Box>
            </Box>

            {/* The reply bar, only while the Roleplay is waiting for its one answer: typed, or spoken. */}
            {item && !answered && !finished && !sending && (
              <Box
                sx={{
                  flexShrink: 0,
                  px: '130px',
                  pb: '24px',
                  // Phone: pinned above the home indicator.
                  [MOBILE_SX]: {
                    px: '12px',
                    pt: '10px',
                    pb: 'max(10px, env(safe-area-inset-bottom))',
                    borderTop: `1px solid ${t.borderDim}`
                  }
                }}
              >
                <Box sx={{ maxWidth: 1146, mx: 'auto' }}>
                  {voiceDenied && (
                    <Typography sx={{ fontSize: 13, color: t.textTertiary, textAlign: 'right', mb: '8px' }}>
                      Voice isn’t available here, so I’ve switched to text.
                    </Typography>
                  )}
                  <Box key={voiceMode ? 'voice' : 'text'} sx={fadeInSx}>
                    {voiceMode ? (
                      <VoiceBar
                        state={voice.state}
                        pausedDuring={voice.pausedDuring}
                        inputMode={voice.inputMode}
                        partial={voice.partial}
                        heardSpeech={voice.heardSpeech}
                        writing={voice.hearing || voice.transcribing}
                        repeating={repeating}
                        value={draft}
                        onChange={setDraftNow}
                        onSend={voice.send}
                        onToggleMic={voice.toggleMic}
                        onInputMode={voice.setInputMode}
                        onReplay={() => {
                          setRepeating(true);
                          speakSituation(() => setRepeating(false));
                        }}
                        onSkip={() => {
                          voice.skipSpeaking();
                          setRepeating(false);
                        }}
                        onPause={voice.pause}
                        onResume={voice.resume}
                        levelRef={voice.speechLevelRef}
                      />
                    ) : (
                      <InputBar
                        value={draft}
                        onChange={setDraft}
                        onSend={() => sendRoleplay(draft)}
                        onSwitchToVoice={voiceAvailable ? () => setVoiceMode(true) : undefined}
                        switchToVoiceLabel={voiceDenied ? 'Retry voice' : 'Speak answer'}
                        placeholder="Type your answer"
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AssessmentPlayerPage;
